// Utility: toolboxCatalogue.js
// Purpose: One catalogue and selector surface for Strategy Builder toolbox and Tree authoring.

import {
    FiActivity,
    FiArrowDown,
    FiArrowUp,
    FiClock,
    FiHash,
    FiLayers,
    FiLayout,
    FiLink,
    FiLogOut,
    FiShield,
    FiTarget,
} from 'react-icons/fi';
import { NODE_VISUAL_CONFIG } from '../config/nodeVisualConfig';
import { MATH_LOGIC_TYPES } from './nodeUtils';

const configItem = (type, overrides = {}) => {
    const config = NODE_VISUAL_CONFIG[type];
    return {
        id: type,
        name: config.label,
        label: config.label,
        desc: config.description,
        type,
        data: config.defaultData,
        icon: config.icon,
        ...overrides,
    };
};

const configItems = (category) => Object.entries(NODE_VISUAL_CONFIG)
    .filter(([, config]) => config.category === category)
    .map(([type]) => configItem(type));

export const DATA_ITEMS = Object.freeze(configItems('data'));
export const VISUAL_ITEMS = Object.freeze(configItems('visual'));
export const MATH_ITEMS = Object.freeze(
    Object.entries(NODE_VISUAL_CONFIG)
        .filter(([, config]) => MATH_LOGIC_TYPES.includes(config.category))
        .map(([type]) => configItem(type)),
);

export const POSITIONS = Object.freeze({
    long: Object.freeze([
        { id: 'long_entry', label: 'Long Entry', name: 'Long Entry', type: 'position_trigger', icon: FiArrowUp, data: { action: 'enter_long', label: 'Long Entry' } },
        { id: 'long_sl', label: 'Stop Loss', name: 'Stop Loss', type: 'position_trigger', icon: FiShield, data: { action: 'sl', type: 'long', label: 'Stop Loss (L)' } },
        { id: 'long_tp', label: 'Take Profit', name: 'Take Profit', type: 'position_trigger', icon: FiTarget, data: { action: 'tp', type: 'long', label: 'Take Profit (L)' } },
        { id: 'long_time', label: 'Time Exit', name: 'Time Exit', type: 'position_trigger', icon: FiClock, data: { action: 'time_exit', type: 'long', label: 'Time Exit (L)' } },
        { id: 'long_exit', label: 'Long Exit', name: 'Long Exit', type: 'position_trigger', icon: FiLogOut, data: { action: 'exit_long', label: 'Long Exit' } },
    ]),
    short: Object.freeze([
        { id: 'short_entry', label: 'Short Entry', name: 'Short Entry', type: 'position_trigger', icon: FiArrowDown, data: { action: 'enter_short', label: 'Short Entry' } },
        { id: 'short_sl', label: 'Stop Loss', name: 'Stop Loss', type: 'position_trigger', icon: FiShield, data: { action: 'sl', type: 'short', label: 'Stop Loss (S)' } },
        { id: 'short_tp', label: 'Take Profit', name: 'Take Profit', type: 'position_trigger', icon: FiTarget, data: { action: 'tp', type: 'short', label: 'Take Profit (S)' } },
        { id: 'short_time', label: 'Time Exit', name: 'Time Exit', type: 'position_trigger', icon: FiClock, data: { action: 'time_exit', type: 'short', label: 'Time Exit (S)' } },
        { id: 'short_exit', label: 'Short Exit', name: 'Short Exit', type: 'position_trigger', icon: FiLogOut, data: { action: 'exit_short', label: 'Short Exit' } },
    ]),
});

export const OUTPUT_ITEMS = Object.freeze([
    { id: 'main_chart', name: 'Main Chart', label: 'Main Chart', desc: 'Price Overlay', type: 'chart_output', data: { panel: 'main', label: 'Main Chart' }, icon: FiLayers },
    { id: 'sub_chart', name: 'Sub Chart', label: 'Sub Chart', desc: 'New Panel', type: 'chart_output', data: { panel: 'new', label: 'Sub Chart' }, icon: FiLayout },
]);

const POSITION_ITEMS = Object.freeze([...POSITIONS.long, ...POSITIONS.short]);
const ENTRY_ACTIONS = new Set(['enter_long', 'enter_short']);
const EXIT_ACTIONS = new Set(['exit_long', 'exit_short']);
const RISK_ACTIONS = new Set(['sl', 'tp', 'time_exit']);

export const filterToolboxIndicatorsByRole = (indicators = [], isSuperuser = false) => (
    isSuperuser
        ? indicators
        : indicators.filter((indicator) => String(indicator.category || '').toUpperCase() !== 'MODEL')
);

const asIndicatorItem = (item) => ({
    ...item,
    type: 'indicator',
    data: {
        ...(item.data || {}),
        indicator: item.data?.indicator || item.id,
        label: item.data?.label || item.name || item.label || item.id,
    },
});

const normalizeSection = (section) => String(section?.id || section || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

export const sectionItems = (section, filteredIndicators = []) => {
    const sectionId = normalizeSection(section);
    if (sectionId === 'entry') return POSITION_ITEMS.filter((item) => ENTRY_ACTIONS.has(item.data.action));
    if (sectionId === 'exit') return POSITION_ITEMS.filter((item) => EXIT_ACTIONS.has(item.data.action));
    if (sectionId === 'risk') return POSITION_ITEMS.filter((item) => RISK_ACTIONS.has(item.data.action));
    if (sectionId === 'chart') return [...DATA_ITEMS, ...VISUAL_ITEMS, ...OUTPUT_ITEMS];
    if (sectionId === 'not-used') {
        return [...filteredIndicators.map(asIndicatorItem), ...MATH_ITEMS];
    }
    return [];
};

const logicVariant = (id, label, operator) => {
    const item = configItem('logic_gate');
    return {
        ...item,
        id,
        name: label,
        label,
        data: {
            ...item.data,
            operator,
            label,
            inputs: operator === 'not' ? item.data.inputs.slice(0, 1) : item.data.inputs,
        },
    };
};

export const groupItems = () => [
    configItem('comparator'),
    configItem('time_window'),
    logicVariant('logic_all', 'ALL', 'and'),
    logicVariant('logic_any', 'ANY', 'or'),
    logicVariant('logic_not', 'NOT', 'not'),
];

export const pickerItems = (filteredIndicators = []) => [
    { id: 'price', label: 'Price', type: 'price', icon: FiActivity, items: [] },
    { id: 'existing', label: 'Existing indicator', type: 'existing', icon: FiLink, items: [] },
    { id: 'new-indicator', label: 'New indicator', type: 'new-indicator', icon: FiActivity, items: filteredIndicators.map(asIndicatorItem) },
    { id: 'math', label: 'Math', type: 'math', icon: NODE_VISUAL_CONFIG.math.icon, items: MATH_ITEMS.filter((item) => item.type === 'math') },
    { id: 'constant', label: 'Constant', type: 'constant', icon: FiHash, items: [] },
];

export const toolboxItems = (filteredIndicators = []) => [
    ...sectionItems('Chart'),
    ...sectionItems('Not used', filteredIndicators),
    ...sectionItems('Entry'),
    ...sectionItems('Exit'),
    ...sectionItems('Risk'),
];
